import ListItem from '@mui/material/ListItem';
import React, { createRef, useEffect } from 'react';
import { Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import { Popup } from 'reactjs-popup';
import Container from '../component/container';
import Row from '../component/row';
import Title from '../component/title';
import { useQuery } from '@tanstack/react-query';
import { authActions } from '@/redux/slice/slice.auth';
import Button from './../component/button';
import { useAppDispatch, useAppSelector } from './../redux';
import { toast } from 'react-toastify';
import ENV from './../config/environment';
import { designationCodes } from '@/utils';
import { AppHeaderInterface } from '@/patterns/template/common/AppHeader';
import { lazyImport } from '@/utils/lazyLoadUtility';
import { apiCallerV2 } from '@/service/service/service';

const useStoreTicketEncryptionToken = () => {
	const isLoggedIn = useAppSelector(state =>
		RESTRICTED_ROLES.includes(state.auth.userProfile?.user_role_id)
			? false
			: state.auth.authenticated,
	);

	const { data: ticketingData } = useQuery({
		queryKey: ['user-ticketing', 'GET', 'v1/ticketing/encrypt'],
		queryFn: apiCallerV2,
		enabled: isLoggedIn,
	});

	useEffect(() => {
		if (ticketingData) {
			localStorage.setItem('user-ticket', JSON.stringify(ticketingData));
		}
	}, [ticketingData]);
};

// Lazy load the LoginPage page
const LoginPage = lazyImport(() => import('@/page/loginPageV3'));
const RESTRICTED_ROLES = [11, 18]; // you can move this to a constants file

const ProtectedRoutes = lazyImport(() => import('./protectedRoutes'));
const AppRoutes = () => {
	const userRoleId = useAppSelector(
		state => state.auth.userProfile?.user_role_id,
	);
	const isRestricted = RESTRICTED_ROLES.includes(userRoleId);

	React.useEffect(() => {
		if (isRestricted) {
			localStorage.removeItem('user-data');
		}
	}, [isRestricted]);

	const isLoggedIn = useAppSelector(state =>
		RESTRICTED_ROLES.includes(state.auth.userProfile?.user_role_id)
			? false
			: state.auth.authenticated,
	);

	const profile = useAppSelector(state =>
		RESTRICTED_ROLES.includes(state.auth.userProfile?.user_role_id)
			? null
			: state.auth.userProfile,
	);

	const { screenId, recordId, platformId } = useAppSelector(
		state => state.screenRecord,
	);
	const _popup = createRef<any>();
	const actionDispatch = useAppDispatch();
	const navigate = useNavigate();

	function onLogoutClick() {
		_popup.current?.close();
		actionDispatch(authActions.logout());
		sessionStorage.clear();
		localStorage.clear();
		navigate('/login');
	}

	function printUserSpecificName(userName: any) {
		const nameArray = userName.split(' ');
		let newName = nameArray[0];
		if (newName.length + nameArray[1] <= 12) {
			newName = newName + ' ' + nameArray[1];
		}
		return newName;
	}

	function onAddTicket() {
		const userTicketString = localStorage.getItem('user-ticket');
		let myToken;
		if (userTicketString) {
			myToken = JSON.parse(userTicketString);
			const url = `${ENV.TICKETING_URL}?params=${JSON.stringify({
				id: encodeURIComponent(platformId),
				key: encodeURIComponent(myToken.encrypted_token),
				screenId: screenId,
				recordId: recordId,
				addTicket: true,
				productId: encodeURIComponent(myToken.product_id),
			})}`;
			window.open(url);
			// useAppDispatch(setScreenAndRecord({screenId: null, recordId: null}))
		} else {
			toast('Ticket Token Missing', { type: 'error' });
		}
	}

	useStoreTicketEncryptionToken();

	function onViewTicket() {
		const userTicketString = localStorage.getItem('user-ticket');
		let myToken;
		if (userTicketString) {
			myToken = JSON.parse(userTicketString);
			const url = `${ENV.TICKETING_URL}?params=${JSON.stringify({
				id: encodeURIComponent(platformId),
				key: encodeURIComponent(myToken.encrypted_token),
				productId: encodeURIComponent(myToken.product_id),
			})}`;
			window.open(url);
			// useAppDispatch(setScreenAndRecord({screenId: null, recordId: null}))
		} else {
			toast('Ticket Token Missing', { type: 'error' });
		}
	}

	let appHeaderActions: AppHeaderInterface['actions'] = null;
	// Removed with respect to Jira ticket https://farmart.atlassian.net/browse/FMVE-1134
	// removed after the last commit 20a6fc4
	if (isLoggedIn) {
		appHeaderActions = {
			onAddTicket,
			onViewTicket,
			handleLogout: () => _popup.current?.open(),
			profile: {
				name: printUserSpecificName(profile?.name),
				mobileNumber: profile?.mobile_number,
				designation:
					designationCodes[profile?.user_role_id as keyof typeof designationCodes] ??
					'N/A',
			},
		};
	}

	return (
		<div
			style={{
				display: 'flex',
				flexGrow: 1,
				width: '100%',
				height: '100vh',
				overflowX: 'hidden',
			}}
		>
			<Routes>
				<Route path='/login' element={<LoginPage />} />
				<Route path='/' element={<Navigate to='/login' />} />
				<Route path='/home' element={<Navigate to='/login' />} />
				<Route path='/index' element={<Navigate to='/login' />} />
				<Route path='*' element={<Navigate to='/protected' />} />
				<Route
					path='/protected/*'
					element={<ProtectedRoutes actions={appHeaderActions} />}
				/>
			</Routes>
			<div>
				<ListItem sx={{ display: 'block', paddingBottom: '15px' }} disablePadding>
					<Popup ref={_popup} modal>
						{(close: () => void) => (
							<Container padding='l'>
								<Title style={{ marginBottom: '32px' }}>Are you Sure ?</Title>
								<Row>
									<Button text='Logout Now' onClick={onLogoutClick} />
									<Button
										style={{ marginLeft: '18px', backgroundColor: 'gray' }}
										text='Cancel'
										onClick={close}
									/>
								</Row>
							</Container>
						)}
					</Popup>
				</ListItem>
			</div>
		</div>
	);
};

export default AppRoutes;
